import type { Content } from 'pdfmake/interfaces'
import { headerCupsService } from '@/services'
import type { IHeaderCups } from '@/modules/Administracion/interfaces/tablas/IHeaderCups'
import type { IConfCups } from '@/modules/Contratacion/interfaces/tablas/IConfCups'
import { tipoImpresionAsistencialDefault } from '../constantes'

export const NOTA_PIE_TIPOS_HABILITADOS: string[] = [
  tipoImpresionAsistencialDefault.LABORATORIOS,
  tipoImpresionAsistencialDefault.HISTORIA,
  tipoImpresionAsistencialDefault.PROCEDIMIENTOS,
  tipoImpresionAsistencialDefault['OTROS SERVICIOS']
]

export const SERVICIO_MEDICAMENTO = 'MEDICAMENTO'

export interface INotaPieParams {
  cupsCodes: string[]
  servicioId?: number | null
  servicioNombre?: string | null
}

const cupsCodeDeNota = (n: IHeaderCups): string | undefined => {
  const cup = Array.isArray(n.cups) ? n.cups[0] : (n.cups as IConfCups | undefined)
  return cup?.cocuCups ?? undefined
}

const normalizaNombre = (v?: string | null): string => (v || '').trim().toUpperCase()

/**
 * Resuelve la nota (pie de página) que aplica a un documento, según el conjunto
 * de CUPS impresos:
 *  - Si se imprime UN único CUPS  → se busca la nota de ese CUPS.
 *  - Si se imprimen VARIOS CUPS   → se omite la nota por CUPS (se usa la default).
 *  - Fallbacks: nota por defecto del servicio (mismo servicio, sin cups) → nota
 *    default global (sin cups ni servicio).
 * Solo considera notas activas (cohcEstado === 1) que tengan descripción.
 *
 * @returns el texto de la nota, o null si no aplica ninguna.
 */
export const getNotaPie = async (
  params: INotaPieParams
): Promise<string | null> => {
  try {
    const { cupsCodes, servicioId, servicioNombre } = params

    const { data: registro } = await headerCupsService.list()
    const todas: IHeaderCups[] = registro?.body || []

    const notas = todas.filter(
      (n) => n.cohcEstado === 1 && !!n.cohcDescripcion
    )

    let nota: IHeaderCups | undefined

    if (cupsCodes.length === 1) {
      const code = cupsCodes[0]
      nota =
        notas.find((n) => cupsCodeDeNota(n) === code && (!servicioId || n.coseId === servicioId)) ||
        notas.find((n) => cupsCodeDeNota(n) === code)
    }

    if (!nota && servicioNombre) {
      const objetivo = normalizaNombre(servicioNombre)
      nota = notas.find(
        (n) => normalizaNombre(n.servicio?.coseNombre) === objetivo && !n.cocuId
      )
    }
    if (!nota && servicioId) {
      nota = notas.find((n) => n.coseId === servicioId && !n.cocuId)
    }

    if (!nota) {
      nota = notas.find((n) => n.cohcIsPrincipal === 1)
    }

    return nota?.cohcDescripcion || null
  } catch {
    return null
  }
}

/**
 * Bloque pdfmake para renderizar la nota de pie de página (arriba de la firma).
 */
export const getNotaPieContent = (texto: string): Content => ({
  margin: [0, 4, 0, 2],
  table: {
    widths: ['*'],
    body: [
      [
        {
          text: texto,
          style: 'notaPie',
          fillColor: '#f7f7f7',
          border: [false, false, false, false],
          margin: [6, 2, 6, 2]
        }
      ]
    ]
  }
})
